import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Size a textarea to its content: collapse to "auto" so it can shrink,
 * then grow to scrollHeight (capped at maxHeight, after which it scrolls).
 *
 * scrollHeight excludes borders but Tailwind's preflight makes textareas
 * border-box, so the border widths are added back or the last line clips.
 * `holder` (the wrapper) is pinned at its current height while measuring
 * so the momentary collapse never shortens the page and jumps the scroll.
 */
export function fitToContent(el, maxHeight, holder) {
  if (!el) return;
  const prevMin = holder ? holder.style.minHeight : "";
  if (holder) holder.style.minHeight = `${holder.offsetHeight}px`;
  el.style.height = "auto";
  const border = (el.offsetHeight - el.clientHeight) || 0;
  const needed = el.scrollHeight + border;
  const capped = maxHeight && needed > maxHeight;
  el.style.height = `${capped ? maxHeight : needed}px`;
  el.style.overflowY = capped ? "auto" : "hidden";
  if (holder) holder.style.minHeight = prevMin;
}

/**
 * A textarea that starts at one row and grows with its content.
 *
 * Being a real <textarea> is what makes Enter insert a newline: HTML only
 * performs implicit form submission from single-line inputs, so Enter here
 * never submits the surrounding form and no Shift+Enter is needed.
 */
const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(
  { value, rows = 1, maxHeight, style, ...props },
  ref,
) {
  const inner = useRef(null);
  const holder = useRef(null);
  const setRefs = useCallback((node) => {
    inner.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  useLayoutEffect(() => { fitToContent(inner.current, maxHeight, holder.current); }, [value, maxHeight]);

  // Wrapping (and so the needed height) changes whenever this field's own
  // width does — window resize, sidebar collapse, column re-layout. Refit
  // on width changes only, so our own height change can't loop.
  useEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w === lastWidth) return;
      lastWidth = w;
      fitToContent(el, maxHeight, holder.current);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxHeight]);

  return (
    <div ref={holder} style={{ width: "100%" }}>
      <textarea
        ref={setRefs}
        rows={rows}
        value={value}
        style={{ resize: "none", overflowY: "hidden", ...style }}
        {...props}
      />
    </div>
  );
});

export default AutoGrowTextarea;
